import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, NavigationStart, Router, UrlTree } from '@angular/router';

import { NavigationHistoryStack, NavigationKind, isFormPath } from '../utils/navigation-history.util';

/** Telas de autenticação e erro nunca são destino do "Voltar" (formulários também não — ver isFormPath). */
const NON_RETURNABLE_PATHS = new Set([
  '/signin', '/login', '/login-2', '/login-3', '/signup', '/otp', '/forgot-password', '/forgot-password-2',
  '/forgot-password-3', '/password-strength', '/reset-password', '/reset-password-2', '/reset-password-3',
  '/reset-password-invalid', '/register', '/register-2', '/register-3', '/email-verification',
  '/two-step-verification', '/success', '/lock-screen', '/error-404', '/error-500', '/change-password',
  '/auth/ms-callback',
]);

const TAB_ID_PREFIX = 'gmcore-tab-';
const STORAGE_KEY_PREFIX = 'navigation-history:';

/**
 * "Voltar" para a página de onde o usuário veio, com os filtros dela (URL completa), funcionando
 * igual no navegador e no PWA. Sem página de origem — aberto direto por link/notificação, ou o
 * PWA recém-aberto, onde o histórico do navegador está vazio — vai para o destino fixo informado
 * pela tela, o mesmo de antes.
 *
 * A pilha fica no sessionStorage (sobrevive ao F5) com uma chave por aba: o navegador copia o
 * sessionStorage pra aba aberta a partir desta, mas não o histórico — sem a chave por aba, a aba
 * nova acharia que tem pra onde voltar.
 */
@Injectable({ providedIn: 'root' })
export class NavigationHistoryService {
  private stack = new NavigationHistoryStack();
  private pendingKind: NavigationKind | 'skip' = 'push';
  private started = false;
  private storageKey = '';

  constructor(private readonly router: Router, private readonly location: Location) {}

  /** Começa a registrar as navegações — chamado uma vez no arranque do app. */
  public init(): void {
    if (this.started) return;
    this.started = true;

    this.storageKey = STORAGE_KEY_PREFIX + this.resolveTabId();
    this.stack = new NavigationHistoryStack(this.load());
    if (isFreshLoad()) this.stack.markNewLoad();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const extras = this.router.getCurrentNavigation()?.extras;
        this.pendingKind = extras?.skipLocationChange
          ? 'skip'
          : event.navigationTrigger === 'popstate'
            ? 'popstate'
            : extras?.replaceUrl ? 'replace' : 'push';
      } else if (event instanceof NavigationEnd) {
        if (this.pendingKind === 'skip') return;
        this.stack.record(event.urlAfterRedirects, this.pendingKind);
        this.save();
      }
    });
  }

  /**
   * Volta para a página de origem; sem origem, navega para <paramref name="fallback"/> (rota ou
   * comandos do router — o destino fixo que a tela usava antes).
   */
  public back(fallback: string | readonly unknown[] | null | undefined): void {
    const target = this.stack.backTarget(isNonReturnable);

    if (!target) {
      this.navigateTo(fallback ?? '/');
      return;
    }

    // Desta carga do app: volta pelo próprio histórico, quantos passos forem (trocas de aba/filtro
    // no meio incluídas) — o botão/gesto de voltar do aparelho segue coerente, sem entrada extra.
    // De uma carga anterior: navega direto pra URL — o histórico do navegador pode ter páginas de
    // fora do app no meio.
    if (target.inHistory) {
      this.location.historyGo(-target.steps);
    } else {
      void this.router.navigateByUrl(target.url);
    }
  }

  /** App aberto como PWA instalado (janela própria, sem botão de voltar do navegador). */
  public isStandalone(): boolean {
    try {
      const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
      return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
    } catch {
      return false;
    }
  }

  /** UrlTree do destino fixo — usado pelo href do link de voltar (abrir em nova aba). */
  public fallbackTree(fallback: string | readonly unknown[] | null | undefined): UrlTree {
    if (fallback == null) return this.router.parseUrl('/');
    return typeof fallback === 'string'
      ? this.router.parseUrl(fallback)
      : this.router.createUrlTree(fallback as unknown[]);
  }

  private navigateTo(fallback: string | readonly unknown[]): void {
    void this.router.navigateByUrl(this.fallbackTree(fallback));
  }

  private resolveTabId(): string {
    try {
      if (!window.name.startsWith(TAB_ID_PREFIX)) {
        window.name = TAB_ID_PREFIX + Math.random().toString(36).slice(2) + Date.now().toString(36);
      }
      return window.name;
    } catch {
      return TAB_ID_PREFIX + 'default';
    }
  }

  private load(): string[] {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((u): u is string => typeof u === 'string') : [];
    } catch {
      return [];
    }
  }

  private save(): void {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.stack.urls));
    } catch {
      // sessionStorage indisponível (modo privado / quota): a pilha vale só até recarregar.
    }
  }
}

/** Carga nova do app (link, URL digitada, PWA aberto) — não F5 nem voltar/avançar do navegador. */
function isFreshLoad(): boolean {
  try {
    const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return !entry || entry.type === 'navigate' || entry.type === 'prerender';
  } catch {
    return true;
  }
}

function isNonReturnable(path: string): boolean {
  return NON_RETURNABLE_PATHS.has(path) || isFormPath(path);
}

